'use client';

import React from 'react';
import CtaButton from './CtaButton';
import Text from './Text';
import { useSignupCtas, useSignupModeState } from '@/components/utils/SignupModeProvider';
import { useCtaLinkProps } from '@/components/utils/useCtaLinkProps';
import type { CtaTarget } from '@/lib/signupMode';
import { cn } from '@/lib/twUtils';

export interface SignupCtaGroupProps {
  /** Fondo su cui vive il gruppo: cambia la variante della CTA secondaria */
  on?: 'light' | 'dark';
  size?: 'lg' | 'md' | 'sm';
  /**
   * Seconda CTA della pagina (es. «Quanto costa»): compare solo quando la
   * modalità di registrazione non ne impone già una.
   */
  fallback?: CtaTarget | null;
  /** Mostra solo la CTA principale */
  primaryOnly?: boolean;
  /** Bottoni a piena larghezza, sempre impilati */
  block?: boolean;
  /** Da dove parte la CTA: finisce nell'evento GTM della demo */
  source?: string;
  className?: string;
}

/**
 * Coppia di CTA del sito, allineata alla modalità di registrazione decisa dal
 * backend: «Prenota una demo» e/o «Prova gratis». Un solo punto da toccare per
 * cambiare le CTA ovunque.
 */
export const SignupCtaGroup = ({
  on = 'light',
  size = 'lg',
  fallback,
  primaryOnly = false,
  block = false,
  source = 'section',
  className,
}: SignupCtaGroupProps) => {
  const { primary, secondary } = useSignupCtas();
  const { isSettled } = useSignupModeState();
  const ctaProps = useCtaLinkProps(source);
  const second = primaryOnly ? null : (secondary ?? fallback ?? null);

  return (
    <div
      // Finché non sappiamo la modalità i bottoni ci sono ma non si vedono:
      // occupano già il loro posto, quindi niente balza quando compaiono, e
      // nessuno legge «Prova gratis» per mezzo secondo prima che diventi
      // altro. `ds-cta-pending` li riaccende per chi non ha JavaScript.
      data-cta-pending={!isSettled || undefined}
      className={cn(
        'flex gap-3.5 transition-opacity duration-200',
        block ? 'flex-col' : 'flex-col sm:flex-row sm:flex-wrap',
        !isSettled && 'invisible opacity-0',
        className
      )}
    >
      <CtaButton {...ctaProps(primary)} variant="accent" size={size} block={block}>
        {primary.label}
      </CtaButton>
      {second && (
        <CtaButton
          {...ctaProps(second)}
          variant={on === 'dark' ? 'onDark' : 'outline'}
          size={size}
          block={block}
        >
          {second.label}
        </CtaButton>
      )}
    </div>
  );
};

export interface SignupNoteProps {
  tone?: React.ComponentProps<typeof Text>['tone'];
  className?: string;
}

/** Rassicurazione sotto le CTA, coerente con la modalità attiva. */
export const SignupNote = ({ tone = 'subtle', className }: SignupNoteProps) => {
  const { note } = useSignupCtas();
  const { isSettled } = useSignupModeState();
  return (
    <Text
      size="note"
      tone={tone}
      data-cta-pending={!isSettled || undefined}
      className={cn(
        'transition-opacity duration-200',
        !isSettled && 'invisible opacity-0',
        className
      )}
    >
      {note}
    </Text>
  );
};
